import { useCallback } from "react";

import { FlowVersion } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import {
  TemplateWithVersion,
  useAllCaseVersionsCombinedWithTemplates,
} from "src/cases/queries/templates";
import { CaseTemplateVersionPublishState } from "src/clients/case-management";
import { FlowVersionStatus } from "src/clients/flow-api";
import { BuildOpenModalActionFn } from "src/flow/useFlowVersionModals";
import {
  CaseTemplateDependency,
  getUsedCaseTemplateVersions,
} from "src/flow/utils/getUsedCaseTemplateVersions";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";

export type UnpublishedTemplateDependency = CaseTemplateDependency & {
  template: TemplateWithVersion["template"] | undefined;
  version: TemplateWithVersion["version"] | undefined;
  status: "draft" | "archived";
};

type Args = {
  flowId?: string;
  buildOpenModalAction: BuildOpenModalActionFn;
};

export const usePublishFlowVersionCallback = ({
  flowId,
  buildOpenModalAction,
}: Args) => {
  const { refetch: refetchFlow } = useFlow(flowId, {
    refetchOnMount: false,
    enabled: false,
  });

  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  // Fetch all templates to check their status (only if case management is enabled)
  const { refetch: refetchTemplates } = useAllCaseVersionsCombinedWithTemplates(
    {
      enabled: false,
    },
  );

  return useCallback(
    async (version: FlowVersion) => {
      const { data: refetchedFlow } = await refetchFlow();
      if (!refetchedFlow) return;

      const versionWithChildFlows = refetchedFlow.versions.find(
        (v) => v.id === version.id,
      );

      if (!versionWithChildFlows) return;

      const hasUnpublishedChildFlowVersions =
        versionWithChildFlows.child_flows?.some((childFlow) =>
          childFlow.versions?.some(
            (version) => version.status !== FlowVersionStatus.PUBLISHED,
          ),
        ) ?? false;

      if (hasUnpublishedChildFlowVersions) {
        buildOpenModalAction(
          "unpublished_child_flows",
          versionWithChildFlows,
        )();
        return;
      }

      // Check 2: Unpublished case templates (only if case management is enabled)
      if (isCaseManagementEnabled) {
        const { data: refetchedTemplates } = await refetchTemplates();

        const templateDeps = getUsedCaseTemplateVersions(version);
        const unpublishedTemplates = templateDeps
          .map((dep) => {
            const templateWithVersion = refetchedTemplates?.find(
              (t) =>
                t.templateId === dep.templateId &&
                t.versionId === dep.versionId,
            );

            // If the template or version is not found, it means it has been deleted/archived
            if (!templateWithVersion) {
              return {
                templateId: dep.templateId,
                versionId: dep.versionId,
                template: undefined,
                version: undefined,
                status: "archived",
              };
            }

            const publishState =
              templateWithVersion.version.attributes.publish_state;
            const isArchived = templateWithVersion.version.attributes.archived;

            if (
              isArchived ||
              publishState !== CaseTemplateVersionPublishState.PUBLISHED
            ) {
              return {
                templateId: dep.templateId,
                versionId: dep.versionId,
                template: templateWithVersion.template,
                version: templateWithVersion.version,
                status: isArchived ? ("archived" as const) : ("draft" as const),
              };
            }

            return null;
          })
          .filter(Boolean) as UnpublishedTemplateDependency[];

        if (unpublishedTemplates.length > 0) {
          buildOpenModalAction(
            "unpublished_case_templates",
            undefined,
            unpublishedTemplates,
          )();
          return;
        }
      }

      buildOpenModalAction("publish_version", versionWithChildFlows)();
    },
    [
      buildOpenModalAction,
      refetchFlow,
      refetchTemplates,
      isCaseManagementEnabled,
    ],
  );
};
